import { createHtmlElementFromString, formatText } from '../../utils/html.utils';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, SecondLevelMenuItem } from '../../api/menu-api';
import { taxonomies } from '../../taxonomies.module';
import { PageModule } from '../../utils/page.module';
import { header } from '../header/header.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { TaxonomyCategory } from '../../api/taxonomies-api';
import { scrollToTop } from '../../utils/helper.utils';
import { HamiltonResourceDocument, HamiltonResourceResult, HamiltonResourceResults } from '../../api/search-api';
import { resourceTile } from '../tiles/resource-tile/resource-tile.module';
import { getAllResources } from '../../api/resources-api';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { isScrollingAllowed } from '../../utils/body-scroll.utils';
import { newsArchive, NewsView } from './news-archive.module';
import { getCatalogDetailPageUrl } from '../../api/general-api';

/**
 * Initializes and renders the news.
 */
export class NewsArchiveList extends PageModule {
  /**
   * Selector of news view
   */
  componentSelector = '.news-list-view';

  /**
   * Markup for the news view
   */
  template = `<div class="news-list-view">
      <aside class="filter__aside filter__aside--light">
        <div class="filter-sidebar">
        </div>
    </aside>
    <div class="filter filter--dark filter--open" id="news-archive-filter">
      <div class="filter__content">
        <div class="news-archive-list"></div>
        <div class="indicators indicators--loading">
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
        </div>
      </div>
    </div>
  </div>`;

  /**
   * Template for the language dropdown of the filter.
   */
  languageDropdownTemplate = `
  <div class="filter-section">
      <label class="form-control__label-outside" for="filter-dropdown"></label>
      <div class="form-control form-control--dropdown">
          <button class="button button--dropdown" id="filter-dropdown"></button>
          <ul class="dropdown" data-reference-id="filter-dropdown">
          </ul>
      </div>
      <input type="checkbox" class="checkbox" id="news-archive-language-filter" name="news-archive-language-filter" value="0">
      <label class="label text-color-grey" for="news-archive-language-filter"></label>
  </div>`;

  /**
   * URL to retrieve news detail pages.
   */
  newsUrl: string;

  /**
   * Template for a row in the language dropdown of the filter.
   */
  languageDropdownItemTemplate = '<li class="dropdown__item" tabindex="0"></li>';

  /**
   * Stores the news results.
   */
  newsResults: HamiltonResourceResults = null;

  /**
   * List of currently selected taxonomies IDs (from the filter).
   */
  currentFilters: string[] = [];

  /**
   * Current search phrase that the user typed into the search field.
   */
  currentSearchPhrase: string = null;

  /**
   * The ventilator ID that the user selected in the ventilator dropdown in the header.
   */
  currentVentilatorId: string = null;

  /**
   * Currently selected first level menu item.
   */
  currentFirstLevelMenuItem: FirstLevelMenuItem = null;

  /**
   * Currently selected second level menu item.
   */
  currentSecondLevelMenuItem: SecondLevelMenuItem = null;

  /**
   * Parameter that filters the available news and must be specified with each API call.
   */
  paths = '';

  languageList: {
    /**
     * The language code of the language that should
     * be shown in the language dropdown initially.
     */
    defaultLanguage: string;

    /**
     * Key is a 2 digit language code (e.g. 'en'), the
     * value is the translated name of the language
     * that needs to be displayed in the language dropdown.
     */
    languages: { [key: string]: string }[];
  };

  /**
   * Variable to prevent multiple calls to load more news. Is set to false
   * as long as new news are loaded from the API.
   */
  isLoadingNews = false;
  /**
   * Variable that indicates if all data that is necessary for this page has been loaded already.
   * E.g. if the taxonomy categories and menu items have been loaded. This is helpful for only
   * rendering the filter when all data is available.
   */
  wasInitialized = false;
  /**
   * Keeps track of how many news are loaded/shown at the moment.
   */
  currentNumberOfResults = 10;

  /**
   * Number of news to load when the user reaches the end of the news list.
   */
  resultNumberStepSize = 10;

  /**
   * When the user reaches the bottom of the page minus this distance in px, new news
   * get loaded automatically.
   */
  loadingDistanceToBottom = 400;

  /**
   * Number of news that should be loaded in the beginning on different breakpoints. Is also the
   * step size which determines how many news will be loaded on scroll when the user reaches the end
   * of the page.
   */
  numberOfProductsToDisplay = {
    large: 20,
    desktop: 9,
    tablet: 6,
    phone: 4
  };

  /**
   * Prefix that is used for IDs in the language filter dropdown.
   */
  languageDropdownIdPrefix = 'language-dropdown-option-';
  /**
   * ID for the 'all' option inside the language filter dropdown.
   */
  languageDropdownIdAllLanguages = 'all';

  /**
   * The currently selected language, that is used to filter news.
   */
  currentNewsLanguage: string;

  /**
   * If the checkbox "include news in English" below the
   * language dropdown is checked or not.
   */
  includesNewsInEnglish = false;

  /**
   * The taxonomy categories.
   */
  taxonomyCategories: TaxonomyCategory[] = [];

  /**
   * Markup for a section in the filter.
   * Contains an accordion that toggles the filter options (taxonomies)
   * that belong to this filter section (taxonomy category).
   */
  filterSectionMarkup = `
<div class="filter-section">
    <div class="accordion accordion--filter">
        <button class="accordion__header"></button>
        <div class="accordion__content">
            <div class="interactive-tags interactive-tags--multiselect">
            </div>
        </div>
    </div>
</div>`;

  /**
   * Empty state for the news tiles when no tiles are available.
   */
  newsEmptyState = `
<div class="news-list-view__empty-state">
    <h4 class="news-list-view__empty-state-title"><span class="text-color-light"></span></h4>
    <span class="news-list-view__empty-state-message"></span>
    <div class="button-section button-section--small-margin">
      <button class="button">
        <span class="button__text"></span>
      </button>
    </div>
</div>`;

  /**
   * Markup for the input element of a filter option.
   */
  filterTagInputMarkup = '<input type="checkbox">';

  /**
   * Markup for the label element of a filter option.
   */
  filterTagLabelMarkup = '<label><span></span><span class="tag tag--counter"></span></label>';

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   * @param paths Paths parameter for news-list requests.
   * @param languageList: List of languages to display in the language dropdown (filter).
   * @param newsUrl URL that can be used to retrieve the contents of the detail pages for news.
   */
  constructor(
    translations: { [key: string]: string },
    paths: string,
    languageList: {
      languages: { [key: string]: string }[];
      defaultLanguage: string;
    },
    newsUrl: string
  ) {
    super();
    this.translations = translations;
    this.paths = paths;
    this.languageList = languageList;
    this.newsUrl = newsUrl;
  }

  /**
   * Update the number of news that should be loaded depending on the current breakpoint
   */
  resetNumberOfNewsBeingLoaded(): void {
    const breakpoint = currentBreakpoint();
    if (breakpoint.isLarge) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.large;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.large;
    } else if (breakpoint.isDesktop) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.desktop;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.desktop;
    } else if (breakpoint.isTablet) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.tablet;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.tablet;
    } else {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.phone;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.phone;
    }
  }

  /**
   * Create the news archive. Fetches the news information and renders the news tiles.
   */
  renderView(): void {
    // show the sub nav
    header.showSubNav(true);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(false);

    // set the page title
    document.title = this.translations.headerTitle;

    // Add to top button with translation
    const content = this.pageComponent.querySelector<HTMLElement>('.filter__content');
    content.dataset.toTop = this.translations.toTop;

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();
    this.setupFilterTranslations();

    // now fetch the news
    this.resetNumberOfNewsBeingLoaded();
    this.showLoadingIndicator(true);
    this.updateFilterPosition();

    // First of all retrieve the taxonomies and their categories
    taxonomies.getCachedTaxonomyCategories().then(async (taxonomyCategories) => {
      this.taxonomyCategories = taxonomyCategories;
      this.loadFiltersFromUrl();

      // check if the URL contains a language that should be
      // selected in the filter language dropdown
      this.loadSelectedNewsLanguageFilter();

      // now fetch the news
      this.fetchNewsData(this.resultNumberStepSize).then((news) => {
        if (!this.wasInitialized) {
          this.setupFilter();
        }

        this.wasInitialized = true;
        this.newsResults = news;
        this.renderNewsTiles();

        // create listener for infinite scrolling
        this.setupInfiniteScrollListener();
        this.renderFilter();

        formatText(this.pageComponent);
      });
    });
  }

  /**
   * Updates the height and position of the filter and filter content area to fill the remaining
   * space below the main navigation and tab bars.
   */
  updateFilterPosition(): void {
    // update the filter position
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
    if (!isMobile) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const mainNavigationSticky =
        document.querySelector<HTMLElement>('.main-navigation-wrapper').style.position === 'sticky';
      scrollTop === 0
        ? header.updateFilterPositionDocumentTop()
        : mainNavigationSticky
        ? header.updateFilterPositionScrolledUp()
        : header.updateFilterPositionScrolledDown();
    }
  }

  /**
   * Reset all filters. Removes all filters that can be selected in the filter sidebar. Updates the URL and re-renders the news results.
   */
  resetFilters(): void {
    this.currentFilters = [];
    this.currentNewsLanguage = this.languageDropdownIdAllLanguages;

    // also remove the selected news language
    this.updateFiltersInUrl();
    writeToUrl({ param: Param.RESOURCE_LANGUAGE, value: this.languageDropdownIdAllLanguages }, null, true, true);

    this.renderView();
  }

  /**
   * Fill the filter element with the translated labels.
   */
  setupFilterTranslations(): void {
    const filterElement = this.pageComponent.querySelector<HTMLElement>('.filter');
    filterElement.dataset.labelButton = this.translations.filterShowAll;
    filterElement.dataset.labelFilter = this.translations.filter;
    filterElement.dataset.labelReset = this.translations.mobileResetFilter;
  }

  /**
   * If the current URL includes any filters, load them so only the filtered news are shown to the user.
   */
  loadFiltersFromUrl(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters that need to be displayed
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
        }
      }
    }

    // load the ventilator from the URL
    const ventilatorId = readUrlParameter(Param.VENTILATOR);
    if (ventilatorId) {
      this.currentVentilatorId = ventilatorId;
    }

    // finally clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Check if the URL contains a language code, that should be selected in the
   * language filter dropdown of the news filter.
   */
  loadSelectedNewsLanguageFilter(): void {
    const newsLanguageFromUrl = readUrlParameter(Param.RESOURCE_LANGUAGE);

    if (newsLanguageFromUrl) {
      this.currentNewsLanguage = newsLanguageFromUrl;
    } else if (this.languageList.defaultLanguage) {
      // If no language in URL -> select the default language if available
      this.currentNewsLanguage = this.languageList.defaultLanguage;
      writeToUrl({ param: Param.RESOURCE_LANGUAGE, value: this.currentNewsLanguage }, null, true);
    }
    this.checkIfIncludeEnglishNewsNeedToBeIncluded();
  }

  /**
   * The "include news in English" checkbox should be selected by default only if
   * the selected language is not English/all and there was no state selection in the URL
   */
  checkIfIncludeEnglishNewsNeedToBeIncluded(): void {
    const includeNewsEnFromUrl = readUrlParameter(Param.INCLUDE_RESOURCES_EN);
    const includeEnglishNewsByDefault =
      includeNewsEnFromUrl === 'true' ||
      (this.currentNewsLanguage &&
        this.currentNewsLanguage !== this.languageDropdownIdAllLanguages &&
        this.currentNewsLanguage !== 'en' &&
        includeNewsEnFromUrl === null);

    this.includesNewsInEnglish = includeEnglishNewsByDefault;
  }

  /**
   * Get the filter category IDs for all filters that should be displayed for the current combination of selected
   * category and selected tab (first & second level menu item).
   */
  getFilterCategoriesForCurrentMenuSelection(): TaxonomyCategory[] {
    const filterCategories = [];
    const filterIds = new Set();
    for (const category of newsArchive.menuItems) {
      for (const tab of category.secondaryCategories) {
        for (const categoryId of tab.filters) {
          filterIds.add(categoryId);
        }
      }
    }

    for (const categoryId of filterIds) {
      filterCategories.push(this.taxonomyCategories.find((category) => category['@id'] === categoryId));
    }
    return filterCategories;
  }

  /**
   * Create skeletons that display a loading state for the filter.
   */
  renderFilterLoadingState(): void {
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      for (let i = 0; i < 3; i++) {
        const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
        const accordion = filterSection.querySelector('.accordion');
        accordion.classList.add('accordion--skeleton');
        const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
        tagContainer.classList.add('interactive-tags--skeleton');

        for (let i = 0; i < 4; i++) {
          this.createFilterTag('', null, false, false, '', '', tagContainer, true);
        }

        // finally append the filter section to the filter sidebar
        filterContent.appendChild(filterSection);
      }
    }
  }

  /**
   * Called when the user selected a ventilator from the ventilator dropdown.
   * @param ventilatorId: Id of the chosen ventilator.
   * @param reload: Boolean if the page should reload the news after the item changed.
   */
  async updateFilterWithNewVentilator(ventilatorId: string, reload = true): Promise<void> {
    if (this.currentVentilatorId !== ventilatorId) {
      this.currentVentilatorId = ventilatorId;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * Called when the user typed in something in the search bar.
   * @param searchPhrase: Free text search from the user.
   * @param reload: Boolean if the page should reload the news after the item changed.
   */
  async updateFilterWithNewSearchPhrase(searchPhrase: string, reload = true): Promise<void> {
    if (this.currentSearchPhrase !== searchPhrase) {
      this.currentSearchPhrase = searchPhrase;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * When the user selects a menu item from the first level, this function is called to update the news
   * to only those, that belong to the selected menu item.
   * @param firstLevelMenuItem: The selected menu item.
   * @param reload: Boolean if the page should reload the news after the item changed.
   */
  async updateFirstLevelMenuSelection(firstLevelMenuItem: FirstLevelMenuItem, reload = true): Promise<void> {
    this.currentFirstLevelMenuItem = firstLevelMenuItem;
    this.currentSecondLevelMenuItem = null;

    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }

    if (firstLevelMenuItem === null) {
      this.updateSecondLevelMenuSelection(null, reload);
    }
  }

  /**
   * When the user selects a menu item from the second level, this function is called to update the news
   * to only those, that belong to the selected menu item.
   *
   * @param secondLevelMenuItem: The selected menu item or null if the 'all' option was selected.
   * @param reload: Boolean if the page should reload the news after the item changed.
   */
  async updateSecondLevelMenuSelection(secondLevelMenuItem: SecondLevelMenuItem, reload = true): Promise<void> {
    this.currentSecondLevelMenuItem = secondLevelMenuItem;
    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }
  }

  /**
   * Load the news and filters depending on the current filter settings.
   */
  async updateFilterSettings(): Promise<void> {
    // first remove filters, that are not applicable to the current menu selection
    this.removeInapplicableFilters();

    // now fetch the news
    this.showLoadingIndicator(true);
    this.resetNumberOfNewsBeingLoaded();
    this.newsResults = await this.fetchNewsData(this.resultNumberStepSize);
    await this.renderNewsTiles();
    return;
  }

  /**
   * When the user switches between menus (categories) or sub menus (tabs), the
   * filters change. This function removes any filter from the URL and currentFilters
   * that are not applicable to the current menu selection anymore.
   */
  removeInapplicableFilters(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters for the current category/tab
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    // we need to remove all filters that are not applicable to the current
    // menu selection from the url. This list contains only the valid filters
    // that will be added to the url after clearing it.
    const newFiltersForUrl = [];

    // add all filters that are valid in the current selected category
    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
          newFiltersForUrl.push(taxonomyId);
        }
      }
    }

    // finally clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Setup listeners for the filter.
   */
  setupFilter(): void {
    // initialize listener for the reset button on mobile
    const resetButton = document.querySelector('.filter-header__reset-button');
    resetButton?.addEventListener('click', () => {
      this.resetFilters();
    });

    // listen for breakpoint changes between mobile and desktop
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.updateFilterPosition();
    });
  }

  /**
   * Render the filter sidebar for the given second level menu item.
   */
  renderFilter(): void {
    // Scroll to top, because news are reloaded
    scrollToTop();

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();

    // To find out which filters we should display for the selected second level menu item,
    // we need to look up the taxonomies from the IDs that we get from the second level menu item.
    // First fetch all taxonomies/taxonomy categories. Then look up the necessary filters.

    // remove all existing filters
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      // create the language dropdown
      this.createLanguagesDropdown(filterContent);

      // collect the filters that need to be displayed
      const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();

      for (const taxonomyCategory of filterCategories) {
        const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
        const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
        const categoryLabel = taxonomyCategory.label || taxonomyCategory.name || taxonomyCategory['@name'];
        filterSection.querySelector<HTMLElement>('.accordion__header').innerText = categoryLabel;
        const categoryId = taxonomyCategory['@id'];

        // 'All' tag
        const allTagId = `${taxonomyCategory['@id']}-all`;

        // Total number of news that belong to this category
        const taxonomyCategoryCount = taxonomyCategory.taxonomies.reduce(
          (totalCount, taxonomy) => totalCount + (this.newsResults.taxonomyCounts[taxonomy['@id']] || 0),
          0
        );

        // Check if the filter category has a selected filter, otherwise preselect the 'all' option
        const hasSelectedOption = taxonomyCategory.taxonomies.reduce(
          (hasActiveFilter, taxonomy) => hasActiveFilter || this.currentFilters.includes(taxonomy['@id']),
          false
        );

        // only if the filter category contains any results, we render it
        if (taxonomyCategoryCount > 0) {
          // create the filter tag for 'all results'
          this.createFilterTag('All', null, !hasSelectedOption, true, allTagId, categoryId, tagContainer);

          // and for each filter in this category create a filter tag
          for (const taxonomy of taxonomyCategory.taxonomies) {
            const taxonomyId = taxonomy['@id'];
            const taxonomyCount = this.newsResults.taxonomyCounts[taxonomyId];
            const checked = this.currentFilters.includes(taxonomyId);
            const filterName = taxonomy.label || taxonomy.name || taxonomy['@name'];

            this.createFilterTag(filterName, taxonomyCount || 0, checked, false, taxonomyId, categoryId, tagContainer);
          }

          // finally append the filter section to the filter sidebar
          filterContent.appendChild(filterSection);
        }
      }

      // update the mobile filter button state
      const mobileFilterButton = document.querySelector('.tag--filter');
      const mobileFilterResetButton = document.querySelector('.tag--filter-secondary');
      if (mobileFilterButton) {
        const noFiltersApplied =
          this.currentFilters?.length === 0 &&
          (this.currentNewsLanguage === this.languageDropdownIdAllLanguages || !this.currentNewsLanguage);

        mobileFilterButton.classList.toggle('tag--filter-inactive', noFiltersApplied);
        mobileFilterResetButton.classList.toggle('tag--hidden', noFiltersApplied);
      }
    }
  }

  /**
   * Create the customer dropdown for the filter.
   */
  createLanguagesDropdown(filterContent: HTMLElement): void {
    // render the customer dropdown and label
    const languageDropdown = createHtmlElementFromString(this.languageDropdownTemplate);
    languageDropdown.querySelector<HTMLElement>('.form-control__label-outside').innerText = this.translations.languages;
    const languageCheckbox = languageDropdown.querySelector<HTMLInputElement>('#news-archive-language-filter');
    const languageCheckboxLabel = languageDropdown.querySelector<HTMLElement>('#news-archive-language-filter + label');
    languageCheckboxLabel.innerText = this.translations.includeResourcesInEnglish;

    // For each language create an option in the dropdown
    const dropdownContainer = languageDropdown.querySelector('ul');
    // create an 'all' option
    const customerDropdownOption = createHtmlElementFromString(this.languageDropdownItemTemplate);
    customerDropdownOption.innerText = this.translations.all;
    customerDropdownOption.id = `${this.languageDropdownIdPrefix}${this.languageDropdownIdAllLanguages}`;
    dropdownContainer.appendChild(customerDropdownOption);

    for (const language of this.languageList.languages) {
      const customerDropdownOption = createHtmlElementFromString(this.languageDropdownItemTemplate);
      const [langCode, languageLabel] = Object.entries(language)[0];
      customerDropdownOption.innerText = languageLabel;
      customerDropdownOption.id = `${this.languageDropdownIdPrefix}${langCode}`;
      dropdownContainer.appendChild(customerDropdownOption);
    }

    const dropdownTitle = languageDropdown.querySelector<HTMLElement>('.button--dropdown');
    if (this.currentNewsLanguage) {
      const languageInfo = this.languageList.languages.find(
        (lang) => Object.keys(lang)[0] === this.currentNewsLanguage
      );
      if (languageInfo) {
        // if the requested language exists, retrieve the translation for UI
        const languageTranslation = languageInfo[Object.keys(languageInfo)[0]];

        // if there was a language set in the URL, select that language in the dropdown as well
        dropdownTitle.innerText = languageTranslation;
      } else {
        // If requested language (from URL) does not exist -> select all option
        dropdownTitle.innerText = this.translations.all;
      }
    } else {
      // If no option was preselected from URL or defaultLanguage
      dropdownTitle.innerText = this.translations.all;
    }

    // insert the customer dropdown at the top of the filter
    const firstFilterSection = filterContent.querySelector('.filter-section');
    if (firstFilterSection) {
      filterContent.insertBefore(languageDropdown, firstFilterSection);
    } else {
      filterContent.appendChild(languageDropdown);
    }

    // Listen for changes in the language dropdown
    dropdownContainer.addEventListener('onitemselect', async (event: CustomEvent) => {
      const selectedLangCode = event.detail.target.id.replace(this.languageDropdownIdPrefix, '');
      this.currentNewsLanguage = selectedLangCode;
      writeToUrl({ param: Param.RESOURCE_LANGUAGE, value: selectedLangCode });

      // user changed the language, now check if the "include English news" checkbox
      // must be selected by default (e.g. for languages different from en/all)
      this.checkIfIncludeEnglishNewsNeedToBeIncluded();

      await this.updateFilterSettings();
      this.renderFilter();
    });

    // Listen for changes in the language checkbox that includes English news
    languageCheckbox.addEventListener('change', async () => {
      const includeEnglishResources = languageCheckbox.checked;
      this.includesNewsInEnglish = includeEnglishResources;
      if (includeEnglishResources) {
        writeToUrl({ param: Param.INCLUDE_RESOURCES_EN, value: 'true' });
      } else {
        writeToUrl({ param: Param.INCLUDE_RESOURCES_EN, value: 'false' });
      }
      await this.updateFilterSettings();
      this.renderFilter();
    });

    // The "include news in English" checkbox should only be shown when the user selected
    // a language that is different from English.
    const includeEnglishResourcesCheckboxNecessary =
      this.currentNewsLanguage &&
      this.currentNewsLanguage !== this.languageDropdownIdAllLanguages &&
      this.currentNewsLanguage !== 'en';
    languageCheckbox.classList.toggle('checkbox--hidden', !includeEnglishResourcesCheckboxNecessary);
    languageCheckbox.checked = this.includesNewsInEnglish;
  }

  /**
   * Create a filter tag with the given properties and append it to the given tagContainer element.
   * @param label: Label of the filter option.
   * @param count: Number of results with this filter option. Displayed in a tag right next to the label.
   * @param checked: If this filter option should be checked or not.
   * @param allOption: If this filter option is an 'all' option that deselects all other options when selected.
   * @param id: ID for the input element.
   * @param categoryId: ID of the filter category (taxonomy category) that is used for the 'name' property of the input group.
   * @param tagContainer: HTML element that holds all tags that belong to the same filter group.
   * @param loadingState: If the tag should only represent a loading state.
   */
  createFilterTag(
    label: string,
    count: number,
    checked: boolean,
    allOption: boolean,
    id: string,
    categoryId: string,
    tagContainer: HTMLElement,
    loadingState = false
  ): void {
    const filterSuffix = '-filter';
    const filterId = id + filterSuffix;

    // If the filter option is currently selected
    const filterIsActive = this.currentFilters.includes(id);
    const filterIsDisabled = count === 0;

    // create the tag input element
    const filterTagInput = createHtmlElementFromString(this.filterTagInputMarkup) as HTMLInputElement;
    filterTagInput.toggleAttribute('checked', checked || filterIsActive);
    filterTagInput.name = categoryId;
    filterTagInput.value = filterId;
    filterTagInput.id = filterId;
    filterTagInput.disabled = filterIsDisabled;

    // mark the 'all' option
    if (allOption) filterTagInput.dataset.checkAll = 'true';

    // append the input to the tag container
    tagContainer.appendChild(filterTagInput);

    if (!loadingState) {
      // listen for changes of the checked state
      filterTagInput.addEventListener('change', (event) => {
        const currentTarget = event.currentTarget as HTMLElement;
        const taxonomyId = currentTarget.id.replace(filterSuffix, '');
        if (filterTagInput.checked) {
          // filter was selected
          if (currentTarget.dataset.checkAll === 'true') {
            // if 'all' option selected -> remove all filters from this taxonomy category
            const options = currentTarget.parentElement.querySelectorAll<HTMLInputElement>('input:not(:first-child)');
            for (const option of options) {
              this.currentFilters = this.currentFilters.filter(
                (taxonomyId) => taxonomyId !== option.id.replace(filterSuffix, '')
              );
            }
            this.updateFiltersInUrl();
          } else {
            // add the filter to the current set of filters
            this.currentFilters.push(taxonomyId);

            this.updateFiltersInUrl();
          }
        } else {
          // filter was removed, remove it from the current filters
          this.currentFilters = this.currentFilters.filter((item) => item !== taxonomyId);
          this.updateFiltersInUrl();
        }

        // finally reload the news archive
        this.renderView();
      });
    }

    // create the tag label
    const filterTagLabel = createHtmlElementFromString(this.filterTagLabelMarkup) as HTMLLabelElement;
    filterTagLabel.setAttribute('for', filterId);
    const labelSpan = filterTagLabel.querySelector('span');
    labelSpan.innerText = label;

    // create the tag counter
    const tagCounter = filterTagLabel.querySelector<HTMLElement>('.tag');
    tagCounter.innerText = count + '';

    if (count === null) {
      // remove the count tag if the count passed is null or zero
      // this is used for the 'all' tag for example
      tagCounter.parentElement.removeChild(tagCounter);
    }

    // append the label to the tag container
    tagContainer.appendChild(filterTagLabel);
  }

  /**
   * Update the current URL with the current filter settings.
   */
  updateFiltersInUrl(noHistory = false, noEvent = false): void {
    // write filters from the filter sidebar
    if (this.currentFilters.length > 0) {
      writeToUrl({ param: Param.FILTER, value: this.currentFilters.join(' ') }, null, noHistory, noEvent);
    } else {
      writeToUrl([], [Param.FILTER], noHistory, noEvent);
    }
  }

  /**
   * Renders the tiles for the news (everything except news)
   * @param fromIndex: If provided this function only renders new news tiles from the given index instead of replacing all existing rendered news tiles.
   */
  async renderNewsTiles(fromIndex: number = null): Promise<void> {
    const newsTilesGrid = this.pageComponent.querySelector<HTMLElement>('.news-archive-list');
    const newsResults = this.newsResults?.results;

    if (!fromIndex) {
      newsTilesGrid.innerHTML = '';
    }

    if (newsResults) {
      const startIndex = fromIndex || 0;
      for (let i = startIndex; i < newsResults.length; i++) {
        const result = newsResults[i];

        if (this.newsResults.totalResults <= this.resultNumberStepSize) {
          // in case there are no more news to load, immediately hide the loading indicator
          this.showLoadingIndicator(false);
        }

        let content = '';
        content = resourceTile.renderTileDocument(result as HamiltonResourceDocument, this.translations, true);

        // create the tile
        const newsTileElement = createHtmlElementFromString(content);

        // listen for clicks on the view buttons and redirect to the detail page
        this.setupDetailPageClickListenersForNewsTile(newsTileElement, result);

        // finally append the news tile to the grid
        newsTilesGrid.appendChild(newsTileElement);
      }

      // If there are so few items, that the document does not scroll,
      // the scroll listener (that fetches more news) will not be triggered.
      // Therefore check if the document does scroll at the moment and if not,
      // continue loading more news.
      const lastScrollTop = window.scrollY;
      const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
      const totalDocumentHeight = document.documentElement.scrollHeight;
      // when the menu is open, scroll is blocked on the body
      const scrollingAllowed = isScrollingAllowed();
      if (currentDocumentHeight === totalDocumentHeight && scrollingAllowed) {
        this.loadMoreProducts();
      }
    }

    if (!newsResults || newsResults.length === 0) {
      // render empty state
      this.renderNewsEmptyState(newsTilesGrid, !!this.currentSearchPhrase);
    } else {
      this.removeEmptyState(newsTilesGrid);
    }
  }

  /**
   * Setup click listeners for the news tiles. The view or detail buttons need to link to the detail page.
   */
  setupDetailPageClickListenersForNewsTile(tile: HTMLElement, news: HamiltonResourceResult): void {
    const buttonMobile = tile.querySelector(`.${resourceTile.resourceTileDetailButtonClass}`);
    const buttonDesktop = tile.querySelectorAll(`.${resourceTile.resourceTileDetailButtonClass}`)[1];
    const title = tile.querySelector('.tile__headline');
    const detailButtonClickListener = (): void => {
      // open news page as standalone page
      const encodedTitle = encodeURIComponent(news.title.replaceAll('/', '-').replaceAll('%', '-').replaceAll(' ', '-'));
      const detailPageUrl = getCatalogDetailPageUrl(this.newsUrl, news.nodeid, news.type, encodedTitle);
      window.location.href = detailPageUrl;
    };

    buttonMobile.addEventListener('click', detailButtonClickListener);
    buttonDesktop.addEventListener('click', detailButtonClickListener);
    title?.addEventListener('click', detailButtonClickListener);
  }

  /**
   * Removes the empty state.
   */
  removeEmptyState(newsList: HTMLElement): void {
    newsList.classList.remove('news-list-view--empty');
    const emptyStateElem = newsList.querySelector<HTMLElement>('.news-list-view__empty-state');
    if (emptyStateElem) {
      emptyStateElem.parentElement.removeChild(emptyStateElem);
    }
  }

  /**
   * show or hide the loading indicator.
   */
  showLoadingIndicator(show: boolean): void {
    const loadingIndicator = document.querySelector<HTMLElement>('.filter .indicators--loading');
    if (loadingIndicator) {
      loadingIndicator.style.visibility = show ? 'visible' : 'hidden';
    }
  }

  /**
   * When the user scrolls to the bottom of the page, automatically load more items
   */
  setupInfiniteScrollListener(): void {
    let lastScrollTop = window.scrollY;

    let isThrottling = false;
    const scrollListener = (): void => {
      if (newsArchive.currentView === NewsView.NEWS_LIST) {
        const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
        const totalDocumentHeight = document.documentElement.scrollHeight;

        lastScrollTop = window.scrollY;
        if (!isThrottling) {
          setTimeout(() => {
            if (currentDocumentHeight >= totalDocumentHeight - this.loadingDistanceToBottom) {
              this.loadMoreProducts();
            }
            isThrottling = false;
          }, 16);
        }
        isThrottling = true;
      }
    };

    window.addEventListener('scroll', scrollListener, false);
  }

  /**
   * Call this function to load more news because the user scrolled to the end of the news list.
   */
  loadMoreProducts(): void {
    // now fetch the news
    if (!this.isLoadingNews) {
      const moreProductsAvailable = this.currentNumberOfResults < this.newsResults.totalResults;
      if (moreProductsAvailable) {
        this.isLoadingNews = true;
        const newNumberOfProducts = this.currentNumberOfResults + this.resultNumberStepSize;
        this.fetchNewsData(newNumberOfProducts).then(async (news) => {
          this.newsResults.results = [...this.newsResults.results, ...news.results];
          await this.renderNewsTiles(this.currentNumberOfResults);
          this.currentNumberOfResults += this.resultNumberStepSize;
          this.isLoadingNews = false;
        });
      } else {
        // hide loading indicator
        this.showLoadingIndicator(false);
      }
    }
  }

  /**
   * Render the empty state inside the news list, when no news are displayed.
   */
  renderNewsEmptyState(newsGrid: HTMLElement, forSearch = true): void {
    this.removeEmptyState(newsGrid);
    newsGrid.classList.add('news-list-view--empty');
    const emptyStateElement = createHtmlElementFromString(this.newsEmptyState);
    const subTitle = emptyStateElement.querySelector<HTMLElement>('.news-list-view__empty-state-title span');
    const title = emptyStateElement.querySelector<HTMLElement>('.news-list-view__empty-state-title');
    const message = emptyStateElement.querySelector<HTMLElement>('.news-list-view__empty-state-message');
    const resetButton = emptyStateElement.querySelector<HTMLElement>('.button');
    const resetButtonLabel = resetButton.querySelector<HTMLElement>('span');

    // Insert translations
    if (forSearch) {
      const titleTextNode = document.createTextNode(this.translations.sorry + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResults;
      message.innerText = this.translations.searchForDifferentKeyword;
      resetButtonLabel.innerText = this.translations.resetSearch;
    } else {
      // empty state when no products available because of filters
      const titleTextNode = document.createTextNode(this.translations.sorryFilters + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResultsFiltered;
      message.innerText = this.translations.removeFilters;
      resetButtonLabel.innerText = this.translations.resetAllFilters;
    }

    // listen for clicks on the reset search button
    resetButton.addEventListener(
      'click',
      () => {
        if (this.currentSearchPhrase) {
          newsArchive.resetNewsSearchInputField();
          newsArchive.removeSearchText();
        } else {
          this.resetFilters();
        }
      },
      false
    );

    // hide the loading indicator
    this.showLoadingIndicator(false);

    // and finally append the empty state to the tile grid
    newsGrid.appendChild(emptyStateElement);
  }

  /**
   * Retrieve the news.
   */
  fetchNewsData(limit: number): Promise<HamiltonResourceResults> {
    return new Promise((resolve) => {
      const offset = limit - this.resultNumberStepSize;
      const searchPhraseExisted = this.currentSearchPhrase !== null && this.currentSearchPhrase !== undefined;

      const languageCodes = [];
      // Check by which languages the news should be filtered
      if (this.currentNewsLanguage && this.currentNewsLanguage !== this.languageDropdownIdAllLanguages) {
        languageCodes.push(this.currentNewsLanguage);
        if (this.includesNewsInEnglish && this.currentNewsLanguage !== 'en') {
          languageCodes.push('en');
        }
      }

      getAllResources(
        this.currentFilters,
        this.currentSearchPhrase,
        this.currentFirstLevelMenuItem?.id,
        this.currentSecondLevelMenuItem?.id,
        this.paths,
        this.currentVentilatorId,
        languageCodes,
        this.resultNumberStepSize,
        offset,
        true
      ).then((results) => {
        if (searchPhraseExisted && results) {
          // If the user searched for something, update the number of results for each menu tab
          newsArchive.totalResults = results.totalResults;
          newsArchive.updateSearchResultCountTags(results.totalResults);
        }
        resolve(results);
      });
    });
  }
}
